import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import LegalLinks, { GoogleConsentNotice } from './LegalLinks';
import MfaChallenge from './MfaChallenge';
import { useLanguage } from '../i18n';
import { consumeMfaSignOut } from '../utils/authEvents';

const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const LoginPage = ({ onLogin, onGoogleLogin }) => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigate = useNavigate();
    const { token } = useAuth();
    // 2FA (#333): `{ resolver, email, password, method }` while the second step
    // of a sign-in is on screen — the card renders MfaChallenge instead of the
    // form. `recoveredVia` is set after a recovery code turned 2FA off.
    const [mfa, setMfa] = useState(null);
    const [recoveredVia, setRecoveredVia] = useState(null);
    // Signed out because this session never passed the second factor (a
    // `401 mfa_required`, see App.jsx) — say so once instead of a silent bounce.
    const [mfaSignedOut] = useState(() => consumeMfaSignOut());

    // A fresh sign-in (password, Google, or after the 2FA code) ALWAYS lands on
    // the Main Page (#512 PO review) — never the last conversation or the page
    // the user signed out from.
    useEffect(() => {
        if (token) {
            console.log('LoginPage: User is authenticated, redirecting...');
            navigate('/home', { replace: true });
        }
    }, [token, navigate]);

    const signInWithPassword = async (addr, pw) => {
        setError('');
        setLoading(true);

        try {
            const result = await onLogin(addr, pw);
            // Don't navigate here - the useEffect above will handle redirect
            // once the token is set by onAuthStateChanged
            if (result.mfaRequired) {
                setMfa({ resolver: result.resolver, email: addr, password: pw, method: 'password' });
            } else if (!result.success) {
                setError(result.error || t('auxLoginInvalidCreds', 'Invalid email or password.'));
            }
        } catch (err) {
            setError(t('auxUnexpectedError', 'An unexpected error occurred. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setRecoveredVia(null);
        return signInWithPassword(email, password);
    };

    // A recovery code was accepted: the backend has REMOVED every second factor
    // from the account, so the first factor alone now signs in. With the typed
    // password we do that at once; a Google sign-in has to press the button
    // again (the popup needs a user gesture).
    const handleRecovered = ({ email: recoveredEmail }) => {
        const pending = mfa;
        setMfa(null);
        setRecoveredVia(pending?.method || 'password');
        if (pending?.method === 'password' && pending.password) {
            signInWithPassword(pending.email || recoveredEmail, pending.password);
        } else if (recoveredEmail && !email) {
            setEmail(recoveredEmail);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        setRecoveredVia(null);
        setGoogleLoading(true);

        try {
            const result = await onGoogleLogin();
            // Don't navigate here - the useEffect above will handle redirect
            // once the token is set by onAuthStateChanged
            if (result.mfaRequired) {
                setMfa({ resolver: result.resolver, email: result.email || '', password: null, method: 'google' });
            } else if (!result.success) {
                setError(result.error || t('auxLoginGoogleFailed', 'Google login failed.'));
            }
        } catch (err) {
            setError(t('auxUnexpectedError', 'An unexpected error occurred. Please try again.'));
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        <div className="relative flex h-dvh overflow-y-auto bg-pai-void text-white">
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.64)]" />
            {/* #432: `m-auto` centres the card while it fits and lets the page
                scroll when it does not (a phone held sideways, a short window).
                `items-center` on the parent used to centre it OUTSIDE the
                viewport instead, cutting off the top and the submit button. */}
            <div className="relative w-full max-w-md px-6 py-6 m-auto">
            <div className="w-full p-8 space-y-6 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile">
                <h2 className="text-[28px] font-extrabold tracking-[-0.025em] text-center text-white">{t('auxLoginTitle', 'Login')}</h2>

                {mfa ? (
                    <MfaChallenge
                        resolver={mfa.resolver}
                        email={mfa.email}
                        onRecovered={handleRecovered}
                        onCancel={() => { setMfa(null); setError(''); }}
                    />
                ) : (<>
                {mfaSignedOut && !recoveredVia && (
                    <div data-testid="login-mfa-required-notice" role="status" className="p-4 text-sm text-white/90 border border-pai bg-white/[0.05] rounded-ctl">
                        {t('mfaSignedOutNotice', 'Your account now uses two-step verification. Sign in again to continue.')}
                    </div>
                )}

                {recoveredVia && (
                    <div data-testid="mfa-recovered-notice" role="status" className="p-4 text-sm text-white/90 border border-[rgba(52,199,89,0.4)] bg-[rgba(52,199,89,0.08)] rounded-ctl">
                        {t('mfaRecoveredNotice', 'Two-step verification has been turned off. Set it up again in Settings → Security.')}
                        {recoveredVia === 'google' && (
                            <> {t('mfaRecoveredGoogle', 'Press Continue with Google to sign in again.')}</>
                        )}
                    </div>
                )}

                {error && (
                    <div className="p-4 text-sm text-pai-red border border-[rgba(240,68,56,0.4)] bg-[rgba(240,68,56,0.08)] rounded-ctl" role="alert">
                        {error}
                    </div>
                )}

                {/* Google Sign-In Button */}
                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading || loading}
                    className="flex items-center justify-center w-full h-11 px-4 text-sm font-semibold text-white border border-pai bg-white/[0.05] rounded-ctl hover:bg-white/[0.1] hover:border-pai-strong transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {googleLoading ? (
                        <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-pai-void" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('auxLoginSigningIn', 'Signing in...')}
                        </span>
                    ) : (
                        <>
                            <GoogleIcon />
                            <span className="ml-2">{t('auxLoginContinueGoogle', 'Continue with Google')}</span>
                        </>
                    )}
                </button>

                {/* Google sign-in on this page also CREATES an account when
                    there isn't one, so it carries the same click-wrap as the
                    signup page rather than assuming the user came through it. */}
                <GoogleConsentNotice className="-mt-3" />

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-pai-subtle" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 text-pai-meta bg-transparent">{t('auxLoginOrEmail', 'Or continue with email')}</span>
                    </div>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-pai-secondary">
                            {t('auxEmailAddress', 'Email address')}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="block w-full h-11 px-3 mt-1 pai-input appearance-none focus:outline-none text-sm"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between">
                            <label htmlFor="password" className="block text-sm font-medium text-pai-secondary">
                                {t('commonPassword', 'Password')}
                            </label>
                            <Link
                                to="/forgot-password"
                                data-testid="login-forgot-password-link"
                                className="text-sm font-medium text-pai-blue hover:text-pai-blue-tint"
                            >
                                {t('auxForgotPasswordLink', 'Forgot password?')}
                            </Link>
                        </div>
                        <div className="relative mt-1 touch:mt-3">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full h-11 px-3 pr-10 mt-1 pai-input appearance-none focus:outline-none text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/45 hover:text-white transition-colors"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading || googleLoading}
                        className={`w-full h-11 px-4 text-sm font-semibold rounded-ctl transition-colors focus:outline-none ${
                            loading || googleLoading
                                ? 'bg-white/40 text-pai-void cursor-not-allowed'
                                : 'bg-white text-pai-void hover:bg-white/90'
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-pai-void" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('auxLoginSigningIn', 'Signing in...')}
                            </span>
                        ) : (
                            t('auxLoginSubmit', 'Sign in with Email')
                        )}
                    </button>
                </form>

                <p className="text-sm text-center text-pai-meta">
                    {t('auxLoginNoAccount', "Don't have an account?")}{' '}
                    <Link
                        to="/signup"
                        className="font-semibold text-pai-blue hover:text-pai-blue-tint"
                    >
                        {t('auxSignUp', 'Sign up')}
                    </Link>
                </p>
                </>)}
            </div>
            {/* Legal footer — the policies have to be reachable from every
                unauthenticated screen, not only from inside the app. */}
            <LegalLinks className="mt-6" />
            </div>
        </div>
    );
};

export default LoginPage;
